import { Form, Mentions, Typography } from 'antd';
import { useMemo } from 'react';
import type {
  WorkflowActionSchema,
  WorkflowDecisionSchema,
} from './WorkflowTemplateBuilder.types';

type WorkflowVariableAutocompleteInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  className?: string;
  textArea?: boolean;
  rows?: number;
  additionalOptions?: string[];
  workflowActionSchemas?: Record<string, WorkflowActionSchema>;
  workflowDecisionSchemas?: Record<string, WorkflowDecisionSchema>;
  showLegend?: boolean;
};

export const WorkflowVariableAutocompleteInput = ({
  value,
  onChange,
  placeholder,
  size: _size = 'small',
  disabled,
  className,
  textArea = false,
  rows = 2,
  additionalOptions,
  workflowActionSchemas,
  workflowDecisionSchemas,
  showLegend = false,
}: WorkflowVariableAutocompleteInputProps) => {
  const form = Form.useFormInstance();
  const requiredInputs =
    (Form.useWatch(['workflow', 'required_inputs'], form) as
      | string[]
      | undefined) || [];
  const steps =
    (Form.useWatch(['workflow', 'steps'], form) as
      | Array<{
          id?: string;
          type?: string;
          action?: { type?: string };
          decision?: { type?: string };
        }>
      | undefined) || [];

  const options = useMemo(() => {
    const requiredInputRefs = requiredInputs
      .map((input) => String(input || '').trim())
      .filter(Boolean);

    const stepOutputRefs = steps.flatMap((step) => {
      const stepId = String(step?.id || '').trim();
      if (!stepId) return [];

      if (step.type === 'decision') {
        const decisionType = String(step?.decision?.type || '').trim();
        const decisionSchema = workflowDecisionSchemas?.[decisionType];
        const outputs = Object.keys(decisionSchema?.outputs || {});
        return outputs.map((field) => `${stepId}.${field}`);
      }

      const actionType = String(step?.action?.type || '').trim();
      const actionSchema = workflowActionSchemas?.[actionType];
      const outputs = Object.keys(actionSchema?.outputs || {});
      return outputs.map((field) => `${stepId}.${field}`);
    });

    const additionalRefs = (additionalOptions || [])
      .map((option) => String(option || '').trim())
      .filter(Boolean)
      .map((option) => {
        if (option.startsWith('$')) return option.slice(1);
        return option;
      });

    const merged = Array.from(
      new Set([
        ...requiredInputRefs.map((input) => `.${input}`),
        ...stepOutputRefs,
        ...additionalRefs,
      ])
    );

    return merged.map((option) => {
      const isRequiredInputRef = option.startsWith('.');
      const fullValue = `$${option}`;
      return {
        value: option,
        label: (
          <span
            style={{
              color: isRequiredInputRef ? '#1677ff' : '#d46b08',
              fontWeight: 500,
            }}
          >
            {fullValue}
          </span>
        ),
      };
    });
  }, [
    additionalOptions,
    requiredInputs,
    steps,
    workflowActionSchemas,
    workflowDecisionSchemas,
  ]);

  return (
    <div className="space-y-1">
      <Mentions
        className={className}
        value={value}
        onChange={onChange}
        prefix={['$']}
        options={options}
        rows={textArea ? rows : 1}
        placeholder={placeholder}
        disabled={disabled}
        split=" "
      />
      {showLegend ? (
        <div className="text-[11px] leading-4">
          <Typography.Text style={{ color: '#1677ff' }}>
            Required input: `$.&lt;input&gt;`
          </Typography.Text>
          <Typography.Text className="mx-2 text-[#bfbfbf]">|</Typography.Text>
          <Typography.Text style={{ color: '#d46b08' }}>
            Step output: `$step.field`
          </Typography.Text>
        </div>
      ) : null}
    </div>
  );
};
